import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useRef } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { devWarning } from '@tanium/coreui-utils';
import { constructHistoryState } from './constructHistoryState';
import { useHistoryListener } from './useHistoryListener';

type HistoryUpdateType = 'push' | 'replace';

interface SetHistoryStateOptions {
  /**
   * The type of update to perform when setting state. This overrides the
   * `defaultUpdateType` that was specified via `createUseHistoryState`.
   */
  updateType?: HistoryUpdateType;
}

interface SetStateFunction<T> {
  /**
   * @param previousState The previous state being replaced.
   */
  (previousState: T): T;
}

interface SetHistoryState<T> {
  /**
   * @param value The new state value, or a function that receives the previous
   * state and returns the new state.
   * @param options Options controlling the behavior when setting state.
   */
  (value: T | SetStateFunction<T>, options?: SetHistoryStateOptions): void;
}

export type UseHistoryStateReturnType<T> = [state: T, setState: SetHistoryState<T>];

export interface UseHistoryState {
  /**
   * A hook for persisting state using `history` APIs. The API is mostly
   * equivalent to `useState`, but it only supports state that may be stored in
   * `history` state by the user agent. This often excludes data that is not
   * supported by [the structured clone
   * algorithm](https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm),
   * including functions, DOM nodes, and certain object properties.
   *
   * @param initialState The initial state, or a function returning the initial
   * state. Only used if there is no corresponding state already in history
   * state.
   *
   * If a state initialization function is provided, it may be invoked
   * multiple times before the state is persisted to `history.state`. If the
   * initializer is not deterministic, only the latest returned value will be
   * persisted to `history.state`.
   *
   * @returns A tuple containing:
   * - The stored history state value. This value may change in response to
   *   invocations of the returned setter, or by `popstate` events. Changes to
   *   history state caused by external usage of `history` APIs are not
   *   guaranteed to be reflected; changes to this state should always be
   *   performed via the returned setter.
   * - A function to set the stored history state value.
   */
  <T>(initialState: T | (() => T)): UseHistoryStateReturnType<T>;
  /**
   * A hook for persisting state using `history` APIs. The API is mostly
   * equivalent to `useState`, but it only supports state that may be stored in
   * `history` state by the user agent. This often excludes data that is not
   * supported by [the structured clone
   * algorithm](https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm),
   * including functions, DOM nodes, and certain object properties.
   *
   * @returns A tuple containing:
   * - The stored history state value. This value may change in response to
   *   invocations of the returned setter, or by `popstate` events. Changes to
   *   history state caused by external usage of `history` APIs are not
   *   guaranteed to be reflected; changes to this state should always be
   *   performed via the returned setter.
   * - A function to set the stored history state value.
   */
  <T = undefined>(): UseHistoryStateReturnType<T | undefined>;
}

interface CreateUseHistoryStateOptions {
  /**
   * The default type of update to perform when setting state.
   * - 'replace' signifies that the hook will perform a `replaceState`
   *   operation, replacing the current slice of state in the history stack with
   *   the newest one. This is useful if there are many changes to the state,
   *   and you don't want to overwhelm the history stack.
   * - 'push' signifies that that the hook will perform a `pushState` operation,
   *   cloning the current state and updating the specified slice with the
   *   newest value. This is useful if the user should be able to use the
   *   browser navigation to go back and forth between previous states.
   *
   * Defaults to 'replace'.
   */
  defaultUpdateType?: HistoryUpdateType;
}

const isExpectedHistoryStateShape = (
  state: unknown,
): state is null | undefined | Record<string, unknown> =>
  state === undefined || typeof state === 'object';

// eslint-disable-next-line @typescript-eslint/ban-types -- using Function as base type
const isSetterFunction = <T, U extends Function>(valueOrSetter: T | U): valueOrSetter is U =>
  typeof valueOrSetter === 'function';

/**
 * A factory function for creating a hook that persists state using `history`
 * APIs.
 *
 * @param stateKey A globally unique key to identify the state associated with
 * the returned hook.
 * @param options Options for controlling the behavior of the returned hook.
 *
 * @returns A React Hook for managing a slice of history state.
 */
export const createUseHistoryState = (
  stateKey: string,
  { defaultUpdateType = 'replace' }: CreateUseHistoryStateOptions = {},
) => {
  let latestStoredValue: unknown;
  let latestReadValue: unknown;

  const hasHistoryStateSlice = () => {
    const { state } = window.history;
    return isExpectedHistoryStateShape(state) && !!state && stateKey in state;
  };

  const getHistoryStateSlice = () => {
    const getValue = () => {
      const { state } = window.history;
      if (!isExpectedHistoryStateShape(state)) {
        return undefined;
      }
      const stateSlice = state?.[stateKey];

      // `window.history.state` returns a copy of the stored value. In order to
      // keep referential stability of the state value returned from
      // `useHistoryState`, the user-provided value is returned when it is
      // equivalent to the history state value.
      if (isEqual(stateSlice, latestStoredValue)) {
        return latestStoredValue;
      }

      // Protect against `window.history.state` not being stable when read
      // successively without any intervening `pushState`/`replaceState` calls.
      // This stability is required by `useSyncExternalStore`.
      if (isEqual(stateSlice, latestReadValue)) {
        return latestReadValue;
      }

      return stateSlice;
    };

    const value = getValue();
    latestReadValue = value;
    return value;
  };

  const setHistoryStateSlice = (data: unknown, options?: SetHistoryStateOptions) => {
    devWarning(
      isExpectedHistoryStateShape(window.history.state),
      `Attempting to write to state slice "${stateKey}", but history state is invalid (${window.history.state})`,
    );

    const setter =
      (options?.updateType ?? defaultUpdateType) === 'push' ? 'pushState' : 'replaceState';
    latestStoredValue = data;
    window.history[setter](
      { ...window.history.state, ...constructHistoryState({ [stateKey]: data }) },
      '',
    );
  };

  const useHistoryState: UseHistoryState = <T>(initialValueArg?: T | (() => T)) => {
    type InitialValueType = T | (() => T);

    const onStoreChangeRef = useRef<() => void>();
    const latestInitialValue = useRef<InitialValueType>(initialValueArg as InitialValueType);

    const signalStoreChange = useCallback(() => {
      onStoreChangeRef.current?.();
    }, []);

    useHistoryListener(signalStoreChange);

    const subscribe = useCallback((onStoreChange: () => void) => {
      // The main (un)subscription is handled by `useHistoryListener`.
      onStoreChangeRef.current = onStoreChange;

      return () => {
        onStoreChangeRef.current = undefined;
      };
    }, []);

    const getSnapshot = useCallback(() => getHistoryStateSlice(), []);

    const state = useSyncExternalStore(subscribe, getSnapshot);

    const setHistoryState = useCallback<SetHistoryState<T>>(
      (dataOrSetter, options) => {
        const getOldState = () => {
          if (hasHistoryStateSlice()) {
            // This type assertion is not strictly safe, since we can't assert
            // the underlying type and it could theoretically be changed out
            // from under us at any time. However, for well-behaved code, it
            // should hold.
            return getHistoryStateSlice() as T;
          }

          return isSetterFunction(latestInitialValue.current)
            ? latestInitialValue.current()
            : latestInitialValue.current;
        };

        setHistoryStateSlice(
          isSetterFunction(dataOrSetter) ? dataOrSetter(getOldState()) : dataOrSetter,
          options,
        );
        signalStoreChange();
      },
      [signalStoreChange],
    );

    const hasSlice = hasHistoryStateSlice();

    useEffect(() => {
      latestInitialValue.current = initialValueArg as InitialValueType;
    }, [initialValueArg]);

    useEffect(() => {
      // We explicitly re-verify that there is still no history state slice in
      // case one has been added (e.g., by a `setState` call) after this effect
      // was queued but before we reached this point.
      if (!hasSlice && !hasHistoryStateSlice()) {
        // Always use replace as the initial update type, even if the hook is
        // configured to use push generally.
        setHistoryStateSlice(
          isSetterFunction(latestInitialValue.current)
            ? latestInitialValue.current()
            : latestInitialValue.current,
          { updateType: 'replace' },
        );
        // Although calling `signalStoreChange` causes an additional render, we
        // must signal that the store has changed so that future changes (i.e.,
        // reverting the state) are not erroneously treated as no-ops.
        signalStoreChange();
      }
    }, [hasSlice, signalStoreChange]);

    const getEffectiveState = () => {
      if (hasSlice) {
        // This type assertion is not strictly safe, since we can't assert the
        // underlying type and it could theoretically be changed out from under
        // us at any time. However, for well-behaved code, it should hold.
        return state as T;
      }

      return isSetterFunction(initialValueArg) ? initialValueArg() : initialValueArg;
    };

    return [getEffectiveState(), setHistoryState];
  };

  return useHistoryState;
};
